/**
 * =============================================================================
 * APP.TSX - Application Root & Router Configuration
 * =============================================================================
 * 
 * This is the main entry point for the React application. It sets up:
 * - React Query for server state management
 * - React Router for client-side routing
 * - Global providers (Tooltip, Toast notifications)
 * - Lazy loading for performance optimization
 * - Global Layout wrapper for consistent UI
 * 
 * ARCHITECTURE:
 * ┌─────────────────────────────────────────────────────────────┐
 * │                    QueryClientProvider                      │
 * │  ┌───────────────────────────────────────────────────────┐  │
 * │  │                   TooltipProvider                     │  │
 * │  │  ┌─────────────────────────────────────────────────┐  │  │
 * │  │  │                 BrowserRouter                   │  │  │
 * │  │  │  ┌───────────────────────────────────────────┐  │  │  │
 * │  │  │  │              Layout                       │  │  │  │
 * │  │  │  │  ┌─────────────────────────────────────┐  │  │  │  │
 * │  │  │  │  │          Routes/Pages               │  │  │  │  │
 * │  │  │  │  └─────────────────────────────────────┘  │  │  │  │
 * │  │  │  └───────────────────────────────────────────┘  │  │  │
 * │  │  └─────────────────────────────────────────────────┘  │  │
 * │  └───────────────────────────────────────────────────────┘  │
 * └─────────────────────────────────────────────────────────────┘
 * 
 * ADDING NEW ROUTES:
 * 1. Create your page component in src/pages/
 * 2. Add lazy import below (for non-critical routes)
 * 3. Add Route element in the Routes section
 * 4. IMPORTANT: Add route ABOVE the catch-all "*" route
 * 
 * ROUTE CATEGORIES:
 * - Public routes: /, /search, /listing/:id, legal pages
 * - Auth routes: /auth, /request-password-reset, /reset-password
 * - Protected routes: /dashboard, /profile, /messages, etc.
 * - Admin routes: /admin, /admin/reports, etc.
 * 
 * @see src/components/Layout.tsx - Global layout wrapper
 * @see src/pages/ - All page components
 * =============================================================================
 */

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Layout } from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import AdminRoute from "@/components/AdminRoute";
import AdminVerify from "./pages/AdminVerify";
import { PushOnboardingDialog } from "@/components/PushOnboardingDialog";
import MaintenanceGate from "@/components/MaintenanceGate";


// =============================================================================
// ROUTE IMPORTS
// =============================================================================

/**
 * EAGER LOAD: Landing page
 * The homepage is loaded immediately for best First Contentful Paint (FCP).
 * This is the most common entry point for users.
 */
import Index from "./pages/Index";

/**
 * LAZY LOAD: All other routes
 * These pages are loaded on-demand when the user navigates to them.
 * This significantly reduces the initial bundle size.
 * 
 * Routes are organized by category for easier maintenance.
 */

// -----------------------------------------------------------------------------
// Authentication Routes
// -----------------------------------------------------------------------------
const Auth = lazy(() => import("./pages/Auth"));
const RequestPasswordReset = lazy(() => import("./pages/RequestPasswordReset"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));


// -----------------------------------------------------------------------------
// Listing Routes
// -----------------------------------------------------------------------------
const CreateListing = lazy(() => import("./pages/CreateListing"));
const EditListing = lazy(() => import("./pages/EditListing"));
const Search = lazy(() => import("./pages/Search"));
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));

// -----------------------------------------------------------------------------
// User Account Routes
// -----------------------------------------------------------------------------
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Purchases = lazy(() => import("./pages/Purchases"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const Profile = lazy(() => import("./pages/Profile"));
const MyReviews = lazy(() => import("./pages/MyReviews"));
const Settings = lazy(() => import("./pages/Settings"));
const SavedListings = lazy(() => import("./pages/SavedListings"));
const Account = lazy(() => import("./pages/Account"));

// -----------------------------------------------------------------------------
// Messaging Routes
// -----------------------------------------------------------------------------
const Messages = lazy(() => import("./pages/Messages"));
const Chat = lazy(() => import("./pages/Chat"));

// -----------------------------------------------------------------------------
// Transaction & Review Routes
// -----------------------------------------------------------------------------
const WriteReview = lazy(() => import("./pages/WriteReview"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));

// -----------------------------------------------------------------------------
// Legal & Info Routes
// -----------------------------------------------------------------------------
const Contact = lazy(() => import("./pages/Contact"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const BuyerProtection = lazy(() => import("./pages/BuyerProtection"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const FAQ = lazy(() => import("./pages/FAQ"));

// -----------------------------------------------------------------------------
// Admin Routes
// -----------------------------------------------------------------------------
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminGroup = lazy(() => import("./pages/AdminGroup"));
const AdminReports = lazy(() => import("./pages/AdminReports"));

const AdminChatView = lazy(() => import("./pages/AdminChatView"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminReportedChats = lazy(() => import("./pages/AdminReportedChats"));
const AdminDisputes = lazy(() => import("./pages/AdminDisputes"));
const AdminResolvedDisputes = lazy(() => import("./pages/AdminResolvedDisputes"));
const AdminActiveSettlements = lazy(() => import("./pages/AdminActiveSettlements"));
const AdminUserSettledDisputes = lazy(() => import("./pages/AdminUserSettledDisputes"));
const AdminSalesStats = lazy(() => import("./pages/AdminSalesStats"));
const AdminSoldListings = lazy(() => import("./pages/AdminSoldListings"));
const AdminDealArchive = lazy(() => import("./pages/AdminDealArchive"));
const AdminEconomy = lazy(() => import("./pages/AdminEconomy"));
const AdminLegalShipping = lazy(() => import("./pages/AdminLegalShipping"));
const ShippingTerms = lazy(() => import("./pages/ShippingTerms"));
const AdminSystemLoad = lazy(() => import("./pages/AdminSystemLoad"));
const SecurityMonitor = lazy(() => import("./pages/SecurityMonitor"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminRemoveListing = lazy(() => import("./pages/AdminRemoveListing"));
const AdminDeletedAccounts = lazy(() => import("./pages/AdminDeletedAccounts"));


// -----------------------------------------------------------------------------
// Error Routes
// -----------------------------------------------------------------------------
const NotFound = lazy(() => import("./pages/NotFound"));

// =============================================================================
// REACT QUERY CONFIGURATION
// =============================================================================

/**
 * QueryClient configuration for React Query.
 * Optimized for performance with aggressive caching and smart refetching.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data stays fresh for 2 minutes - reduces unnecessary refetches
      staleTime: 1000 * 60 * 2,
      // Keep unused data in cache for 10 minutes
      gcTime: 1000 * 60 * 10,
      // Only refetch on window focus if data is stale
      refetchOnWindowFocus: 'always',
      // Don't refetch on mount if data is fresh
      refetchOnMount: true,
      // Retry failed queries 2 times with exponential backoff
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// =============================================================================
// LOADING FALLBACK COMPONENT
// =============================================================================

/**
 * Loading spinner shown while lazy-loaded pages are being fetched.
 * Provides visual feedback to users during route transitions.
 */
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// =============================================================================
// MAIN APP COMPONENT
// =============================================================================

/**
 * Root application component.
 * Sets up all providers and defines the routing structure.
 */
const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {/* Toast notifications (shadcn/ui) */}
          <Toaster />
          {/* Sonner notifications (alternative toast library) */}
          <Sonner />
          
          <BrowserRouter>
            {/* Engångsdialog som frågar om push-notiser efter inloggning */}
            <PushOnboardingDialog />
            {/* Maintenance mode: visitors see a holding page, admins keep access */}
            <MaintenanceGate>
            {/* Global Layout: Header + Content + Footer */}
            <Layout>
              {/* Suspense boundary for lazy-loaded routes */}
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {/* =========================================================
                      PUBLIC ROUTES
                      These routes are accessible to all users
                  ========================================================= */}
                  <Route path="/" element={<Index />} />
                  <Route path="/search" element={<Search />} />
                  <Route path="/listing/:id" element={<ListingDetail />} />
                  <Route path="/user/:username" element={<UserProfile />} />
                  <Route path="/bildelar/:brand" element={<CategoryPage />} />
                  <Route path="/bildelar/:brand/:model" element={<CategoryPage />} />
                  
                  {/* =========================================================
                      AUTHENTICATION ROUTES
                      Login, signup, and password reset flows
                  ========================================================= */}
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/request-password-reset" element={<RequestPasswordReset />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

                  
                  {/* =========================================================
                      PROTECTED ROUTES
                      These routes require authentication
                      Note: Auth protection is handled in each page component
                  ========================================================= */}
                  <Route path="/create-listing" element={<CreateListing />} />
                  <Route path="/edit-listing/:id" element={<EditListing />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/purchases" element={<Purchases />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/profile/reviews" element={<MyReviews />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/saved" element={<SavedListings />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/messages" element={<Messages />} />
                  <Route path="/chat/:listingId/:receiverId" element={<Chat />} />
                  <Route path="/review/:transactionId" element={<WriteReview />} />
                  <Route path="/checkout/return" element={<CheckoutReturn />} />
                  
                  {/* =========================================================
                      LEGAL & INFO ROUTES
                      Static pages with legal information
                  ========================================================= */}
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/terms-of-use" element={<TermsOfUse />} />
                  <Route path="/fraktvillkor" element={<ShippingTerms />} />
                  <Route path="/buyer-protection" element={<BuyerProtection />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/faq" element={<FAQ />} />
                  
                  {/* =========================================================
                      ADMIN ROUTES
                      Guarded centrally by <AdminRoute /> (useAdminRole cache)
                  ========================================================= */}
                  <Route path="/admin/verify" element={<AdminVerify />} />
                  <Route element={<AdminRoute />}>
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/group/:key" element={<AdminGroup />} />
                    <Route path="/admin/reports" element={<AdminReports />} />
                    <Route path="/admin/chat/:listingId/:user1Id/:user2Id" element={<AdminChatView />} />
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/reported-chats" element={<AdminReportedChats />} />
                    <Route path="/admin/disputes" element={<AdminDisputes />} />
                    <Route path="/admin/resolved-disputes" element={<AdminResolvedDisputes />} />
                    <Route path="/admin/active-settlements" element={<AdminActiveSettlements />} />
                    <Route path="/admin/user-settled-disputes" element={<AdminUserSettledDisputes />} />
                    <Route path="/admin/sales-stats" element={<AdminSalesStats />} />
                    <Route path="/admin/sold-listings" element={<AdminSoldListings />} />
                    <Route path="/admin/deal-archive" element={<AdminDealArchive />} />
                    <Route path="/admin/economy" element={<AdminEconomy />} />
                    <Route path="/admin/legal-shipping" element={<AdminLegalShipping />} />
                    <Route path="/admin/system-load" element={<AdminSystemLoad />} />
                    <Route path="/security-monitor" element={<SecurityMonitor />} />
                    <Route path="/admin/analytics" element={<AdminAnalytics />} />
                    <Route path="/admin/remove-listing" element={<AdminRemoveListing />} />
                    <Route path="/admin/deleted-accounts" element={<AdminDeletedAccounts />} />
                  </Route>

                  
                  {/* =========================================================
                      CATCH-ALL ROUTE (404)
                      IMPORTANT: This must be the LAST route!
                      Add all new routes ABOVE this line.
                  ========================================================= */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </Layout>
            </MaintenanceGate>
          </BrowserRouter>
          
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
